import { Button } from '@repo/videa-components/src/index';
import type { ColumnDef } from '@tanstack/react-table';
import { useAtomValue } from 'jotai';
import { useHistory, useLocation } from 'react-router';

import { usePreferences } from '@/react-query/usePreferences';
import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

import { isCountsAtom } from '../atoms';
import { calculateBenchmarks } from '../helpers/benchmarking/calculateBenchmarks';
import { useProviderRates } from '../helpers/benchmarking/providerRates';
import { formatLargeNumber } from '../helpers/formatNumber';

import { BenchmarkChip } from './BenchmarkChip';
import { TrendIndicator } from './TrendIndicator';

export type TInsightsTableData = {
  name: string;
  id: string;
  practiceName?: string;
  practiceId?: string;
  completedTreatments?: number;
  opportunity?: number;
  acceptedRate?: number;
  plannedRate?: number;
  previousCompletedTreatments?: number;
  previousOpportunity?: number;
  previousAcceptedRate?: number;
  previousPlannedRate?: number;
  conversionRate?: number;
  previousConversionRate?: number;
  completedRate?: number;
  previousCompletedRate?: number;
  viewedRate?: number;
  previousViewedRate?: number;
};

export const useGetBenchmarkingTableColumns = ({
  drilldownLevel,
}: {
  drilldownLevel: 'Practice' | 'Provider' | 'Region';
}): ColumnDef<TInsightsTableData, unknown>[] => {
  const isCounts = useAtomValue(isCountsAtom);

  const history = useHistory();
  const location = useLocation();
  const { data: prefs } = usePreferences();

  const { trackEvent } = useTrackEvent();

  const providerRates = useProviderRates({
    isDataFiltered: false,
  });

  const caseAcceptanceBenchmark = calculateBenchmarks(
    providerRates.providerRates
      ?.map((provider) => provider.acceptanceRate)
      .filter((rate) => rate !== undefined) ?? [],
  );

  const plannedRateBenchmark = calculateBenchmarks(
    providerRates.providerRates
      ?.map((provider) => provider.plannedRate)
      .filter((rate) => rate !== undefined) ?? [],
  );

  const completedRateBenchmark = calculateBenchmarks(
    providerRates.providerRates
      ?.map((provider) => provider.completionRate)
      .filter((rate) => rate !== undefined) ?? [],
  );

  const conversionRateBenchmark = calculateBenchmarks(
    providerRates.providerRates
      ?.map((provider) => provider.conversionRate)
      .filter((rate) => rate !== undefined) ?? [],
  );

  const handleDrilldown = (id: string, name: string, practiceId?: string) => {
    trackEvent(mixpanelEvents.insightsView, {
      dashboard: 'CLINICAL',
      drilldownLevel,
      drilldown: true,
      id,
      name,
    });
    let path = location.pathname.split('insights')[0] + 'insights/clinical/';

    if (drilldownLevel === 'Region') {
      path += 'region/' + id;
    }
    if (drilldownLevel === 'Practice') {
      const regionId = location.pathname.split('region/')[1]?.split('/')[0];

      path += 'region/' + regionId + '/practice/' + id;
    }
    if (drilldownLevel === 'Provider') {
      const regionId = location.pathname.split('region/')[1]?.split('/')[0];

      path +=
        'region/' + regionId + '/practice/' + practiceId + '/provider/' + id;
    }

    history.push({ pathname: path, search: location.search });
  };

  const columns: ColumnDef<TInsightsTableData, unknown>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <div className="vai-flex vai-flex-col vai-items-start">
          <Button
            variant="link"
            size="link"
            className="vai-w-auto"
            onClick={() =>
              handleDrilldown(
                row.original.id,
                row.original.name,
                row.original.practiceId,
              )
            }
          >
            <span className="vai-font-light vai-underline vai-text-left">
              {row.original.name}
            </span>
          </Button>
          {row.original.practiceName &&
            !location.pathname.includes('practice') && (
              <span className="vai-text-left vai-text-muted-foreground vai-font-light">
                {row.original.practiceName}
              </span>
            )}
        </div>
      ),
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        return (
          rowA.original.name?.localeCompare(rowB.original?.name ?? '') ?? 0
        );
      },
    },
    {
      accessorKey: 'conversionRate',
      header: 'Conversion Rate',
      minSize: 200,
      cell: (props) => {
        const { row } = props;
        const hoveredRowId = (props as any).hoveredRowId;
        const conversionRate = row.original.conversionRate;
        const previousConversionRate = row.original.previousConversionRate;
        const change =
          previousConversionRate && conversionRate
            ? ((conversionRate - previousConversionRate) /
                previousConversionRate) *
              100
            : undefined;
        return (
          conversionRate !== undefined && (
            <span className="vai-flex vai-flex-row vai-gap-2 vai-items-center">
              <BenchmarkChip
                value={conversionRate}
                benchmark={conversionRateBenchmark}
              />

              <>{Math.round(conversionRate * 100)}%</>

              <TrendIndicator
                value={change}
                suffix="%"
                isMinimal={hoveredRowId !== row.id}
              />
            </span>
          )
        );
      },
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        return (
          (rowA.original.conversionRate ?? 0) -
          (rowB.original.conversionRate ?? 0)
        );
      },
    },
    {
      accessorKey: 'plannedRate',
      header: 'Planned Rate',
      cell: (props) => {
        const { row } = props;
        const hoveredRowId = (props as any).hoveredRowId;
        const change =
          row.original.previousPlannedRate && row.original.plannedRate
            ? ((row.original.plannedRate - row.original.previousPlannedRate) /
                row.original.previousPlannedRate) *
              100
            : undefined;
        return (
          row.original.plannedRate !== undefined && (
            <span className="vai-flex vai-flex-row vai-gap-2 vai-items-center">
              <BenchmarkChip
                value={row.original.plannedRate}
                benchmark={plannedRateBenchmark}
                isMicro={true}
              />

              <>{Math.round(row.original.plannedRate * 100)}%</>

              <TrendIndicator
                value={change}
                suffix="%"
                isMinimal={hoveredRowId !== row.id}
              />
            </span>
          )
        );
      },
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        return (
          (rowA.original.plannedRate ?? 0) - (rowB.original.plannedRate ?? 0)
        );
      },
    },
    {
      accessorKey: 'acceptedRate',
      header: 'Scheduled Rate',
      cell: (props) => {
        const { row } = props;
        const hoveredRowId = (props as any).hoveredRowId;
        const change =
          row.original.previousAcceptedRate && row.original.acceptedRate
            ? ((row.original.acceptedRate - row.original.previousAcceptedRate) /
                row.original.previousAcceptedRate) *
              100
            : undefined;
        return (
          row.original.acceptedRate !== undefined && (
            <span className="vai-flex vai-flex-row vai-gap-2 vai-items-center">
              <BenchmarkChip
                value={row.original.acceptedRate}
                benchmark={caseAcceptanceBenchmark}
                isMicro={true}
              />

              <>{Math.round(row.original.acceptedRate * 100)}%</>

              <TrendIndicator
                value={change}
                suffix="%"
                isMinimal={hoveredRowId !== row.id}
              />
            </span>
          )
        );
      },
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        return (
          (rowA.original.acceptedRate ?? 0) - (rowB.original.acceptedRate ?? 0)
        );
      },
    },
    {
      accessorKey: 'completedRate',
      header: 'Completed Rate',
      cell: (props) => {
        const { row } = props;
        const hoveredRowId = (props as any).hoveredRowId;
        const change =
          row.original.previousCompletedRate && row.original.completedRate
            ? ((row.original.completedRate -
                row.original.previousCompletedRate) /
                row.original.previousCompletedRate) *
              100
            : undefined;
        return (
          row.original.completedRate !== undefined && (
            <span className="vai-flex vai-flex-row vai-gap-2 vai-items-center">
              <BenchmarkChip
                value={row.original.completedRate}
                benchmark={completedRateBenchmark}
                isMicro={true}
              />

              <>{Math.round(row.original.completedRate * 100)}%</>

              <TrendIndicator
                value={change}
                suffix="%"
                isMinimal={hoveredRowId !== row.id}
              />
            </span>
          )
        );
      },
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        return (
          (rowA.original.completedRate ?? 0) -
          (rowB.original.completedRate ?? 0)
        );
      },
    },

    {
      accessorKey: 'opportunity',
      header: 'AI Opportunity Available',
      cell: (props) => {
        const { row } = props;
        const hoveredRowId = (props as any).hoveredRowId;
        const change =
          row.original.previousOpportunity && row.original.opportunity
            ? ((row.original.opportunity - row.original.previousOpportunity) /
                row.original.previousOpportunity) *
              100
            : undefined;
        return (
          <span className="vai-flex vai-flex-row vai-gap-2 vai-items-center">
            {row.original.opportunity !== undefined && isCounts
              ? row.original.opportunity
              : formatLargeNumber(row.original.opportunity)}
            <TrendIndicator
              value={change}
              suffix="%"
              decreasingIsPositive={true}
              isMinimal={hoveredRowId !== row.id}
            />
          </span>
        );
      },
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        return (
          (rowA.original.opportunity ?? 0) - (rowB.original.opportunity ?? 0)
        );
      },
    },
    {
      accessorKey: 'completedTreatments',
      header: 'Production',
      cell: (props) => {
        const { row } = props;
        const hoveredRowId = (props as any).hoveredRowId;
        const change =
          row.original.previousCompletedTreatments &&
          row.original.completedTreatments
            ? ((row.original.completedTreatments -
                row.original.previousCompletedTreatments) /
                row.original.previousCompletedTreatments) *
              100
            : undefined;
        return (
          <span className="vai-flex vai-flex-row vai-gap-2 vai-items-center">
            {row.original.completedTreatments !== undefined && isCounts
              ? row.original.completedTreatments
              : formatLargeNumber(row.original.completedTreatments)}
            <TrendIndicator
              value={change}
              suffix="%"
              isMinimal={hoveredRowId !== row.id}
            />
          </span>
        );
      },
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        return (
          (rowA.original.completedTreatments ?? 0) -
          (rowB.original.completedTreatments ?? 0)
        );
      },
    },
  ];

  if (prefs?.enableInsightsEngagementMetric) {
    columns.push({
      accessorKey: 'viewedRate',
      header: 'View Rate',
      cell: (props) => {
        const { row } = props;
        const hoveredRowId = (props as any).hoveredRowId;
        const change =
          row.original.previousViewedRate && row.original.viewedRate
            ? ((row.original.viewedRate - row.original.previousViewedRate) /
                row.original.previousViewedRate) *
              100
            : undefined;
        return (
          row.original.viewedRate !== undefined && (
            <span className="vai-flex vai-flex-row vai-gap-2 vai-items-center">
              {Math.round(row.original.viewedRate * 100)}%
              <TrendIndicator
                value={change}
                suffix="%"
                isMinimal={hoveredRowId !== row.id}
              />
            </span>
          )
        );
      },
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        return (
          (rowA.original.viewedRate ?? 0) - (rowB.original.viewedRate ?? 0)
        );
      },
    });
  }

  return columns;
};
